import {
	CLIP_DURATION_MS_ATTRIBUTE,
	CLIP_MARKER_ATTRIBUTE,
	CLIP_PUBLIC_ID_ATTRIBUTE,
	formatClipDuration,
} from "@seavien/commons";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import logger from "@/lib/logger";
import type { Localizer } from "@/main/react/Types";

/**
 * Turns a server-rendered clip POSTER into something you can press play on — and imports no player
 * code until you do.
 *
 * <p><b>Additive by construction (AGENTS.md rule 32).</b> The poster `<img>` present before enhancement
 * is the same node afterwards, with a wrapper inserted around it and a button appended beside it.
 * Nothing is replaced, removed or re-attributed, so the worst case if this module throws, never runs,
 * or fails to load its chunk is exactly the HTML the server shipped: a still image at the right size.
 * That is the whole reason a clip is STORED as a poster rather than as a `<video>` — see `Clip.ts`.
 *
 * <p><b>Nothing is fetched until a gesture.</b> The player chunk is dynamically imported on the first
 * click, and the MP4 itself is only requested by the `<video>` the chunk creates. An unwatched clip
 * therefore costs one WebP poster and zero JavaScript, which is the strongest form of the
 * `preload="none"` cost lever §7 is built around: on a feed of clips, the bytes track what people
 * actually watch rather than what scrolls past.
 *
 * <p><b>Idempotent, and re-run on every render on purpose.</b> React re-applies
 * `dangerouslySetInnerHTML` whenever the host re-renders with a fresh `{__html}` object, which destroys
 * every wrapper this created. The caller (`useBodyMediaLightbox`) therefore runs it with no dependency
 * array, and this function skips anything it has already enhanced.
 *
 * Design: designs/01-now/2026-08-05-short-loop-video-upload-transcode-and-egress-guardrails-design.md §7
 */

/** Marks a wrapper this module created. Also how the re-run above recognises its own work. */
export const CLIP_CONTAINER_ATTRIBUTE = "data-seavien-clip-container";

/**
 * Set when the player chunk could not be loaded and the poster was kept instead.
 *
 * <p>This is the third of rule 32's three doors — an island's own terminal-failure branch — and it
 * needs its own marker for the reason the rule spells out: NOT rendering a fallback is exactly what
 * stops `assertNoIslandFallback` from firing, and a page that kept its poster looks completely
 * correct. Without a marker, this recovery path would have quietly deleted its own coverage. Asserted
 * by `tests/helpers/islandHealthGuard.mjs`.
 */
export const CLIP_PLAYER_FAILED_ATTRIBUTE = "data-clip-player-failed";

export const CLIP_PLAY_BUTTON_PLAYWRIGHT_ID = "clip-play-button";

/** The stable, kebab-case chunk key required by rule 26. Never a bare `import()`. */
const CLIP_PLAYER_CHUNK_KEY = "clip-player";

type ClipPlayerModule = {
	activateClipPlayer: (options: {
		container: HTMLElement;
		poster: HTMLImageElement;
		clipPublicId: string;
		localizer: Localizer;
		mediaAttentionDiscussionPublicId?: string | null;
	}) => void;
};

/**
 * A clip inside masked (blurred) content must NOT get a play affordance.
 *
 * <p>Sensitivity is applied by wrapping a whole body or card in `SensitiveMediaGuard` — there is no
 * per-image treatment in injected HTML to inherit — so without this check the first click on
 * deliberately blurred content would start playing the video behind it. The guard re-renders its host
 * when the reader reveals, and this function re-runs on every render, so the affordance appears at that
 * point without any coordination between the two.
 */
function isInsideMaskedContent(element: Element): boolean {
	return !!element.closest('[data-sensitive-guard="masked"]');
}

function loadClipPlayer(): Promise<ClipPlayerModule> {
	return importChunkWithRetryAndReload(
		() => import("@/main/react/media/clipPlayer").then((m) => ({ default: m })),
		CLIP_PLAYER_CHUNK_KEY,
	).then((module) => module.default);
}

/**
 * @param mediaAttentionDiscussionPublicId the discussion whose page-instance session the player's play
 *        and watch-through signals credit (round-6 `2c`); handed through untouched, and absent for a body
 *        that is not the post's own (a thought, a card), which the session then drops.
 * @returns how many posters were newly given a play affordance (0 on a re-run over enhanced content)
 */
export function enhanceBodyClipPlayers(
	root: HTMLElement | null | undefined,
	localizer: Localizer,
	mediaAttentionDiscussionPublicId?: string | null,
): number {
	if (!root) {
		return 0;
	}

	let enhanced = 0;

	// A STATIC list, so inserting wrappers below cannot disturb the iteration.
	root.querySelectorAll<HTMLImageElement>(`img[${CLIP_MARKER_ATTRIBUTE}]`).forEach((poster) => {
		if (poster.parentElement?.hasAttribute(CLIP_CONTAINER_ATTRIBUTE)) {
			return; // already ours
		}
		if (isInsideMaskedContent(poster)) {
			return;
		}

		const clipPublicId = poster.getAttribute(CLIP_PUBLIC_ID_ATTRIBUTE)?.trim();
		if (!clipPublicId) {
			// A poster with no id cannot address a video. Leaving it as a plain image is correct — it
			// is still the frame the author chose.
			return;
		}

		const doc = poster.ownerDocument;
		const container = doc.createElement("div");
		container.setAttribute(CLIP_CONTAINER_ATTRIBUTE, "true");
		container.setAttribute(CLIP_PUBLIC_ID_ATTRIBUTE, clipPublicId);
		container.className = "seavien-clip relative block";

		const playLabel = localizer("discussion.card.media.play_here");
		const button = doc.createElement("button");
		button.type = "button";
		button.setAttribute("data-playwright-id", CLIP_PLAY_BUTTON_PLAYWRIGHT_ID);
		button.setAttribute("aria-label", playLabel);
		button.title = playLabel;
		button.className = "seavien-clip-play absolute inset-0 flex items-center justify-center "
			+ "outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset";
		// `border-border`, not a white hairline: the fill is theme-relative, so the edge must be too
		// (BL-136 / round-35 B2, pinned by `bodyClipPlayers.designLanguage.spec.ts`).
		button.innerHTML = '<span class="flex size-14 items-center justify-center rounded-full '
			+ 'border border-border bg-background/80 text-foreground backdrop-blur-sm" aria-hidden="true">'
			+ '<svg viewBox="0 0 24 24" fill="currentColor" class="size-6" focusable="false">'
			+ '<path d="M8 5v14l11-7z"></path></svg></span>';

		const durationRaw = poster.getAttribute(CLIP_DURATION_MS_ATTRIBUTE);
		const durationLabel = formatClipDuration(durationRaw ? Number.parseInt(durationRaw, 10) : null);
		if (durationLabel) {
			// Only when the transcoder has actually measured it. A clip still processing shows no badge
			// rather than "0:00", which would read as an empty clip instead of a pending one.
			const badge = doc.createElement("span");
			badge.className = "seavien-clip-duration pointer-events-none absolute bottom-2 right-2 "
				+ "rounded-full border border-border bg-background/80 px-2 py-0.5 text-xs font-medium "
				+ "text-foreground backdrop-blur-sm";
			badge.setAttribute("data-playwright-id", "clip-duration-badge");
			badge.textContent = durationLabel;
			container.appendChild(badge);
		}

		poster.parentNode?.insertBefore(container, poster);
		container.appendChild(poster);
		container.appendChild(button);

		button.addEventListener("click", (event) => {
			event.preventDefault();
			// Stops a clip inside a linked card from navigating instead of playing.
			event.stopPropagation();

			button.disabled = true;
			loadClipPlayer()
				.then(({ activateClipPlayer }) => {
					activateClipPlayer({ container, poster, clipPublicId, localizer, mediaAttentionDiscussionPublicId });
					button.remove();
				})
				.catch((error) => {
					// Rule 32's third door. The poster is untouched and still on screen, so the page has
					// LESS than nothing subtracted — but a kept poster is indistinguishable from a
					// healthy one, hence the marker.
					container.setAttribute(CLIP_PLAYER_FAILED_ATTRIBUTE, "true");
					button.disabled = false;
					logger.error("[bodyClipPlayers] clip player chunk failed to load", error, {
						clipPublicId,
						chunkKey: CLIP_PLAYER_CHUNK_KEY,
					});
				});
		});

		enhanced += 1;
	});

	return enhanced;
}
